import { createContext, useEffect, useState } from "react"
import { Outlet, useNavigate, useParams } from "react-router-dom"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { Customer, CustomerSite } from "@hydrologiq/gql-experience/graphql"
import { useLazyQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { useRoute } from "@hydrologiq/shared/providers/helpers/useRoute"

type ContextState = {
  loading?: boolean
  customer?: Customer
  site?: CustomerSite
  error?: string
  updateCustomer: (customer: Partial<Customer>) => void
  updateSite: (site: Partial<CustomerSite>) => void
  archive: () => void
}

export const CustomerContext = createContext<ContextState | undefined>(undefined)

export const CustomerProvider = ({ children }: { children?: React.ReactNode }) => {
  const route = useRoute()
  const navigate = useNavigate()
  const { customerId, siteId } = useParams()
  const [customer, setCustomer] = useState<Customer | undefined>(undefined)
  const [site, setSite] = useState<CustomerSite | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [getCustomer, { loading: customerLoading }] = useLazyQueryWithEndpoint<
    gqlTypes.GetCustomerQuery,
    gqlTypes.GetCustomerQueryVariables
  >(gqlTypes.GetCustomerDocument, undefined, "primary")
  const [getSite, { loading: sitesLoading }] = useLazyQueryWithEndpoint<
    gqlTypes.GetCustomerSiteQuery,
    gqlTypes.GetCustomerSiteQueryVariables
  >(gqlTypes.GetCustomerSiteDocument, undefined, "primary")

  useEffect(() => {
    if (customerId) {
      getCustomer({ variables: { id: customerId } })
        .then(({ data }) => {
          if (data && data.customer) {
            setCustomer(data.customer)
          } else {
            setError("Failed to load customer")
          }
        })
        .catch((error) => {
          setError(error.message)
        })
      if (siteId) {
        getSite({ variables: { customerId, siteId } })
          .then(({ data }) => {
            if (data && data.customerSite) {
              setSite(data.customerSite)
            } else {
              setError("Failed to load site")
            }
          })
          .catch((error) => {
            setError(error.message)
          })
      } else {
        setSite(undefined)
      }
    } else {
      setCustomer(undefined)
      setSite(undefined)
      setError(undefined)
    }
  }, [customerId, siteId])
  const value = {
    customer,
    site,
    loading: customerLoading || sitesLoading,
    error,
    updateCustomer: (nCustomer: Partial<Customer>) => {
      setCustomer({ ...customer, ...nCustomer } as Customer)
    },
    updateSite: (nSite: Partial<CustomerSite>) => {
      setSite({ ...site, ...nSite } as CustomerSite)
    },
    archive: () => {
      setCustomer(undefined)
      setSite(undefined)
      navigate(route)
    },
  }

  return <CustomerContext.Provider value={value}>{children ? children : <Outlet />}</CustomerContext.Provider>
}
